/**
 * Manage Page - View, edit, and delete videos
 */

import { createNavigation } from '../components/navigation.js';
import { api } from '../lib/api.js';
import { getCategoryDisplay, t } from '../lib/i18n.js';
import { getCurrentUser } from '../lib/keycloak.js';
import { showConfirm, showModal, showPrompt } from '../lib/modal.js';
import { router } from '../lib/router.js';

export async function renderManage(_params) {
  const app = document.getElementById('app');
  app.innerHTML = '';

  app.appendChild(await createNavigation());

  // Get current user to check if admin (fetches from API to get correct role from database)
  const currentUser = await getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  // Debug logging
  console.log('Current user:', currentUser);
  console.log('Is admin:', isAdmin);

  const main = document.createElement('main');
  main.className = 'admin-main';
  main.innerHTML = `
    <div class="container">
      <div class="mb-lg flex flex-between flex-center">
        <div>
          <h1>${t('manage_title')}</h1>
          <p style="color: var(--color-cloud-grey); margin-top: 8px;">
            ${t('manage_subtitle')}
          </p>
        </div>
        <a href="/upload" class="btn btn-primary" data-link>
          📤 ${t('manage_upload_new')}
        </a>
      </div>
      
      <!-- Filters -->
      <div class="card mb-lg">
        <div class="flex flex-gap-sm">
          <button class="btn btn-secondary active" data-filter="all">${t('manage_filter_all')}</button>
          <button class="btn btn-secondary" data-filter="documentary">${t('manage_filter_documentary')}</button>
          <button class="btn btn-secondary" data-filter="short">${t('manage_filter_short')}</button>
          <button class="btn btn-secondary" data-filter="feature">${t('manage_filter_feature')}</button>
          <button class="btn btn-secondary" data-filter="processing">${t('manage_filter_processing')}</button>
        </div>
      </div>
      
      <!-- Video Grid -->
      <div id="video-grid" class="video-grid">
        <div class="flex" style="justify-content: center; padding: 40px; grid-column: 1/-1;">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
    
    <!-- Edit Modal -->
    <div id="edit-modal" style="display: none;"></div>
  `;

  app.appendChild(main);

  let allVideos = [];
  let currentFilter = 'all';
  let statusPollInterval = null;
  // Captured at mount; lets stale intervals from prior manage mounts detect
  // they're orphaned (the SPA router doesn't fire beforeunload, so old
  // intervals from previous /manage visits would otherwise keep running and
  // mutate the new mount's DOM with stale `allVideos` closures, causing the
  // progress bar to flicker as multiple intervals overwrite each other).
  let mountedGrid = null;

  // Load videos
  async function loadVideos() {
    const grid = document.getElementById('video-grid');
    if (!grid) return; // Page unmounted mid-fetch.
    try {
      const videos = await api.getVideos();
      // Filter out deleted videos
      allVideos = (videos || []).filter((v) => v.state !== 'deleted');
      renderVideos(filterVideos(currentFilter));
    } catch (error) {
      console.error('Failed to load videos:', error);
      const stillThere = document.getElementById('video-grid');
      if (!stillThere) return;
      stillThere.innerHTML = `
        <div style="text-align: center; padding: 40px; grid-column: 1/-1; color: var(--color-error);">
          Failed to load videos. Please try again.
        </div>
      `;
    }
  }

  // Poll status for videos that are processing
  async function pollStatuses() {
    // Bail and self-clean if our captured grid is no longer in the DOM —
    // means this interval is orphaned from a previous mount of this page.
    if (mountedGrid && !document.body.contains(mountedGrid)) {
      if (statusPollInterval) clearInterval(statusPollInterval);
      statusPollInterval = null;
      return;
    }
    try {
      // Find videos that need status updates
      const processingVideos = allVideos.filter(
        (v) => v.state === 'created' || v.state === 'uploaded' || v.state === 'encoding',
      );

      if (processingVideos.length === 0) {
        return; // Nothing to poll
      }

      // Update status for each video
      let stateChanged = false;

      for (const video of processingVideos) {
        try {
          const status = await api.getVideoStatus(video.videoId);

          if (status) {
            // Always update progress info (even if state hasn't changed)
            if (status.progress) {
              video.progress = status.progress;
              // Update just the progress in the DOM without re-rendering
              updateVideoProgress(video);
            }

            // If state changed, update in database
            if (status.state !== video.state) {
              await api.updateVideo(video.videoId, {
                state: status.state,
                published: video.published, // Preserve published status
                approvalStatus: video.approvalStatus, // Preserve approval status
              });
              // Sync local cache so the next poll cycle doesn't see the same
              // mismatch and re-trigger a full reload — /videos/sync only
              // changes PG state when the incoming state is "uploaded", so
              // for transient transitions the local cache is what stops the loop.
              video.state = status.state;
              // Only trigger a full reload when the state moves OUT of the
              // processing set (into ready/failed/etc.), since that's when
              // UI affordances actually change.
              const stillProcessing = ['created', 'uploaded', 'encoding'].includes(status.state);
              if (!stillProcessing) {
                stateChanged = true;
              }
            }
          }
        } catch (error) {
          console.error(`Failed to get status for ${video.videoId}:`, error);
        }
      }

      // Only reload if state changed (no need to reload for progress updates)
      if (stateChanged) {
        await loadVideos();
      }
    } catch (error) {
      console.error('Error polling statuses:', error);
    }
  }

  // Update just the progress elements for a video (no full re-render)
  function updateVideoProgress(video) {
    const card = document.querySelector(`[data-video-id="${video.videoId}"]`);
    if (!card) return;

    // Replace the entire encoding progress section
    const existingProgress = card.querySelector('.encoding-progress');
    if (existingProgress) {
      existingProgress.outerHTML = getVariantProgressDisplay(video);
    } else if (video.state === 'encoding' && video.progress) {
      // Insert progress section if it doesn't exist
      const videoHeader = card.querySelector('.video-header');
      if (videoHeader) {
        videoHeader.insertAdjacentHTML('afterend', getVariantProgressDisplay(video));
      }
    }
  }

  // Start polling
  function startStatusPolling() {
    // Poll immediately
    pollStatuses();

    // Then poll every 5 seconds
    statusPollInterval = setInterval(pollStatuses, 5000);
  }

  // Stop polling when leaving page
  window.addEventListener('beforeunload', () => {
    if (statusPollInterval) {
      clearInterval(statusPollInterval);
    }
  });

  // Filter videos
  function filterVideos(filter) {
    switch (filter) {
      case 'documentary':
        return allVideos.filter(
          (v) =>
            v.category === 'documentary' || v.category === 'documentary_short' || v.category === 'documentary_long',
        );
      case 'short':
        return allVideos.filter((v) => v.category === 'short');
      case 'feature':
        return allVideos.filter((v) => v.category === 'feature');
      case 'processing':
        return allVideos.filter((v) => v.state === 'encoding' || v.state === 'uploaded' || v.state === 'created');
      default:
        return allVideos;
    }
  }

  // Render videos
  // Helper function to calculate time ago
  function getTimeAgo(timestamp) {
    const now = Date.now();
    const uploaded = new Date(timestamp).getTime();
    const diffMs = now - uploaded;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 1) return `${diffDays} days ago`;
    if (diffHours > 1) return `${diffHours} hours ago`;
    if (diffMins > 1) return `${diffMins} minutes ago`;
    return 'Just now';
  }

  function renderVideos(videos) {
    const grid = document.getElementById('video-grid');
    if (!grid) return; // Orphaned interval from a prior mount — defensive.
    grid.innerHTML = '';

    if (videos.length === 0) {
      grid.innerHTML = `
        <div style="text-align: center; padding: 40px; grid-column: 1/-1; color: var(--color-cloud-grey);">
          ${t('manage_no_videos')}
        </div>
      `;
      return;
    }

    videos.forEach((video) => {
      const card = createVideoCard(video, isAdmin);
      grid.appendChild(card);
    });
  }

  // Helper function to generate encoding progress display
  function getVariantProgressDisplay(video) {
    if (video.state !== 'encoding' || !video.progress) {
      return '';
    }

    const percent = Math.max(0, Math.min(100, Number(video.progress.percent) || 0));

    return `
      <div class="encoding-progress">
        <div class="encoding-progress-header">
          <span class="encoding-progress-title">${video.progress.step || 'Encoding video'}</span>
          <span class="encoding-progress-overall">${percent}%</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${percent}%"></div>
        </div>
      </div>
    `;
  }

  // Create video card
  function createVideoCard(video, isAdmin) {
    // Debug: Log video state and admin status for Force Re-Encode button visibility
    if (video.state === 'ready') {
      console.log(
        `[DEBUG] Video ${video.videoId} (${video.originalTitle || video.title}): state='${video.state}', isAdmin=${isAdmin}`,
      );
    }

    const card = document.createElement('div');
    card.className = 'video-card';
    card.setAttribute('data-video-id', video.videoId); // Add ID for targeted updates

    // Create SVG placeholder. Single quotes inside the data URL are encoded
    // as %27 so the value can be embedded inside a single-quoted JS string
    // (the onerror handler below) without terminating it early.
    const videoTitle = (video.originalTitle || video.title || video.fileName || 'Video').substring(0, 30);
    const placeholderSvg = `data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27640%27 height=%27360%27 viewBox=%270 0 640 360%27%3E%3Crect width=%27640%27 height=%27360%27 fill=%27%23232A34%27/%3E%3Ctext x=%2750%25%27 y=%2745%25%27 dominant-baseline=%27middle%27 text-anchor=%27middle%27 fill=%27%230077C0%27 font-family=%27Arial, sans-serif%27 font-size=%2724%27 font-weight=%27bold%27%3E${encodeURIComponent(videoTitle)}%3C/text%3E%3Cpath d=%27M270 140 L270 220 L340 180 Z%27 fill=%27%230077C0%27 opacity=%270.3%27/%3E%3C/svg%3E`;

    // Use still image first (landscape), then poster (portrait), then placeholder.
    // resolveAsset prepends the API base to /images/asset/* so the browser
    // fetches them from the API instead of admin.stroyma.fo (which has no
    // such route — see lib/api.js).
    const thumbnailUrl =
      api.resolveAsset(video.stillUrl) ||
      api.resolveAsset(video.posterUrl) ||
      api.resolveAsset(video.thumbnailUrl) ||
      placeholderSvg;

    card.innerHTML = `
      <div class="video-thumbnail">
        <img src="${thumbnailUrl}" alt="${video.originalTitle || video.title || video.fileName || 'Video'}" onerror="this.src='${placeholderSvg}'">
      </div>
      <div class="video-info">
        <div class="video-header">
          <div class="video-title-section">
            <div class="video-title">${video.originalTitle || video.title || video.fileName || 'Untitled'}</div>
            <div class="video-subtitle">
              ${video.category ? `<span>${getCategoryDisplay(video.category)}</span>` : ''}
              ${video.runtime ? `<span>${Math.floor(video.runtime / 60) > 0 ? `${Math.floor(video.runtime / 60)}t ` : ''}${video.runtime % 60}m</span>` : ''}
              ${video.uploadedAt ? `<span>⏰ ${getTimeAgo(video.uploadedAt)}</span>` : ''}
              ${video.rentalPrice ? `<span style="color: var(--color-sky-blue);">💳 ${(video.rentalPrice / 100).toFixed(2)} kr</span>` : ''}
              ${video.isFaroese ? `<span style="color: var(--color-faroese-amber);">★ Føroyskur</span>` : ''}
            </div>
          </div>
          <div class="video-status-badges">
            ${
              video.state
                ? `<span class="status-badge status-${video.state}">
              ${t(`status_${video.state}`) || video.state}
            </span>`
                : ''
            }
            ${video.published ? `<span class="status-badge" style="background: var(--color-success); color: white;">✓ Published</span>` : `<span class="status-badge" style="background: var(--color-cloud-grey); color: white;">⊗ Unpublished</span>`}
            ${video.approvalStatus === 'approved' ? `<span class="status-badge" style="background: var(--color-success); color: white;">✓ Approved</span>` : video.approvalStatus === 'rejected' ? `<span class="status-badge" style="background: var(--color-error); color: white;">✗ Rejected</span>` : `<span class="status-badge" style="background: var(--color-warning); color: white;">⏳ Pending</span>`}
          </div>
        </div>
        
        ${getVariantProgressDisplay(video)}
        
        ${
          isAdmin
            ? `
          <div class="video-metadata">
            ${video.ownerEmail ? `<div class="metadata-item">📧 <strong>${video.ownerEmail}</strong></div>` : ''}
            ${video.approvedBy ? `<div class="metadata-item">✓ Approved by <strong>${video.approvedBy}</strong></div>` : ''}
            ${video.publishedBy ? `<div class="metadata-item">📤 Published by <strong>${video.publishedBy}</strong></div>` : ''}
          </div>
        `
            : ''
        }
        
        <div class="video-actions">
          <!-- Approval Actions (Pending videos only) -->
          ${
            video.approvalStatus === 'pending' && isAdmin
              ? `
            <div class="action-group">
              <button class="btn btn-small btn-success" data-action="approve" data-id="${video.videoId}">
                ✓ Approve
              </button>
              <button class="btn btn-small btn-danger" data-action="reject" data-id="${video.videoId}">
                ✗ Reject
              </button>
            </div>
          `
              : ''
          }
          
          <!-- Primary Actions -->
          <div class="action-group">
            ${
              video.state === 'ready'
                ? `
              <button class="btn btn-small ${video.published ? 'btn-warning' : 'btn-success'}" data-action="publish" data-id="${video.videoId}" data-published="${video.published || false}">
                ${video.published ? '⊗ Unpublish' : '✓ Publish'}
              </button>
              <button class="btn btn-small btn-primary" data-action="play" data-id="${video.videoId}">
                ▶️ ${t('manage_play')}
              </button>
            `
                : ''
            }
            ${
              (
                video.state === 'created' ||
                  video.state === 'uploaded' ||
                  video.state === 'encoding' ||
                  video.state === 'failed' ||
                  video.state === 'error'
              ) && isAdmin
                ? `
              <button class="btn btn-small btn-warning" data-action="retry-encoding" data-id="${video.videoId}">
                🔄 Retry Encoding
              </button>
            `
                : ''
            }
          </div>
          
          <!-- Secondary Actions -->
          <div class="action-group">
            <button class="btn btn-small btn-secondary" data-action="edit" data-id="${video.videoId}">
              ${t('manage_edit')}
            </button>
            <button class="btn btn-small btn-secondary" data-action="status" data-id="${video.videoId}">
              ${t('manage_status')}
            </button>
          </div>
          
          <!-- Danger/Advanced Actions -->
          <div class="action-group">
            ${
              video.state === 'ready' && isAdmin
                ? `
              <button class="btn btn-small btn-secondary" data-action="force-reencode" data-id="${video.videoId}" title="Re-encode with latest encoder settings (new qualities, formats, etc.)">
                ⚡ Force Re-Encode
              </button>
            `
                : ''
            }
            <button class="btn btn-small btn-danger" data-action="delete" data-id="${video.videoId}">
              ${t('manage_delete')}
            </button>
          </div>
        </div>
      </div>
    `;

    return card;
  }

  // Filter handlers
  document.querySelectorAll('[data-filter]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      document.querySelectorAll('[data-filter]').forEach((b) => b.classList.remove('active'));
      e.target.classList.add('active');
      currentFilter = e.target.dataset.filter;
      renderVideos(filterVideos(currentFilter));
    });
  });

  // Action handlers
  // Bind actions to this mount. Document listeners survive SPA navigation and
  // accumulate on every visit, dispatching several edits/deletes for one click.
  main.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-action][data-id]');
    if (!button || !main.contains(button)) return;
    const action = button.dataset.action;
    const videoId = button.dataset.id;

    if (!action || !videoId) return;

    if (action === 'play') {
      await showPlayerModal(videoId);
    } else if (action === 'edit') {
      router.navigate(`/edit/${videoId}`);
    } else if (action === 'status') {
      await showStatusModal(videoId);
    } else if (action === 'delete') {
      await handleDelete(videoId);
    } else if (action === 'publish') {
      await handlePublish(videoId, button.dataset.published === 'true');
    } else if (action === 'approve') {
      await handleApprove(videoId);
    } else if (action === 'reject') {
      await handleReject(videoId);
    } else if (action === 'retry-encoding') {
      await handleRetryEncoding(videoId);
    } else if (action === 'force-reencode') {
      await handleForceReencode(videoId);
    }
  });

  // Show player modal
  async function showPlayerModal(videoId) {
    try {
      const modal = document.getElementById('edit-modal');

      // Get access token using Keycloak auth
      const authHeaders = await api.getAuthHeaders();
      const tokenResponse = await fetch(`${api.baseUrl}/videos/${videoId}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          userId: currentUser?.id || 'admin-user',
          durationMinutes: 180,
        }),
      });

      if (!tokenResponse.ok) {
        throw new Error('Failed to get video access');
      }

      const { token } = await tokenResponse.json();

      // Get the actual Azure HLS manifest URL
      const streamResponse = await fetch(`${api.baseUrl}/stream/${videoId}/manifest.m3u8?token=${token}`);
      if (!streamResponse.ok) {
        throw new Error('Failed to get stream URL');
      }

      const { manifestUrl } = await streamResponse.json();
      // Relative proxy URLs need the API base URL prepended
      const hlsUrl = manifestUrl.startsWith('/') ? `${api.baseUrl}${manifestUrl}` : manifestUrl;

      console.log('HLS URL:', hlsUrl);
      console.log('Token:', token);

      modal.innerHTML = `
        <div class="modal-overlay">
          <div class="modal-content" style="max-width: 1280px;">
            <div class="modal-header">
              <h2>▶️ Video Player</h2>
              <button class="modal-close">&times;</button>
            </div>
            <div class="modal-body">
              <video id="video-player" controls style="width: 100%; background: black;">
                Your browser does not support video playback.
              </video>
            </div>
          </div>
        </div>
      `;

      modal.style.display = 'block';

      // Load HLS.js from CDN if not already loaded
      if (!window.Hls) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
        script.onload = () => initializePlayer(hlsUrl);
        document.head.appendChild(script);
      } else {
        initializePlayer(hlsUrl);
      }

      function initializePlayer(url) {
        const video = document.getElementById('video-player');

        if (window.Hls?.isSupported()) {
          // For proxy URLs (relative paths), no SAS token propagation needed
          // For legacy Azure URLs, extract SAS token to apply to all requests
          let sasParams = '';
          try {
            const urlObj = new URL(url);
            sasParams = urlObj.search;
          } catch {
            // Relative URL (e.g. /hls-proxy/...) — no SAS params needed
          }

          const hls = new window.Hls({
            xhrSetup: (xhr, requestUrl) => {
              // Append SAS token to ALL HLS requests (playlists + segments)
              if (sasParams && !requestUrl.includes(sasParams)) {
                const separator = requestUrl.includes('?') ? '&' : '?';
                xhr.open('GET', requestUrl + separator + sasParams.substring(1), true);
              }
            },
          });

          hls.loadSource(url);
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
            console.log('Video ready to play with SAS token propagation');
          });
          hls.on(window.Hls.Events.ERROR, (_event, data) => {
            console.error('HLS error:', data);
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Native HLS support (Safari)
          video.src = url;
        }
      }

      // Close modal
      modal.querySelector('.modal-close').addEventListener('click', () => {
        modal.style.display = 'none';
        modal.innerHTML = '';
      });

      modal.querySelector('.modal-overlay').addEventListener('click', (e) => {
        if (e.target.classList.contains('modal-overlay')) {
          modal.style.display = 'none';
          modal.innerHTML = '';
        }
      });
    } catch (error) {
      alert(`❌ Failed to load video: ${error.message}`);
    }
  }

  // Show edit modal
  async function _showEditModal(videoId) {
    try {
      // Get video from database via API
      const video = allVideos.find((v) => v.videoId === videoId);

      if (!video) {
        alert('Video not found');
        return;
      }

      const modal = document.getElementById('edit-modal');

      // Helper to get name values
      const _getName = (obj) =>
        obj ? `${obj.firstName || ''} ${obj.middleName || ''} ${obj.lastName || ''}`.trim() : '';

      modal.innerHTML = `
        <div class="modal-overlay">
          <div class="modal" style="max-width: 900px; max-height: 90vh; overflow: hidden;">
            <div class="modal-header">
              <h2>${t('form_edit_video')}</h2>
              <button class="modal-close" data-action="close-modal">×</button>
            </div>
            <form id="edit-form" style="max-height: calc(90vh - 100px); overflow-y: auto;">
              
              <!-- Category -->
              <div class="form-section">
                <h3 class="form-section-title">${t('form_category')}</h3>
                <div class="form-group">
                  <select id="edit-category" class="form-input">
                    <option value="">${t('form_category')}...</option>
                    <option value="short" ${video.category === 'short' ? 'selected' : ''}>${t('category_short')}</option>
                    <option value="feature" ${video.category === 'feature' ? 'selected' : ''}>${t('category_feature')}</option>
                    <option value="documentary" ${video.category === 'documentary' ? 'selected' : ''}>${t('category_documentary')}</option>
                    <option value="documentary_short" ${video.category === 'documentary_short' ? 'selected' : ''}>${t('category_documentary_short')}</option>
                    <option value="documentary_long" ${video.category === 'documentary_long' ? 'selected' : ''}>${t('category_documentary_long')}</option>
                  </select>
                </div>
              </div>
              
              <!-- Basic Information -->
              <div class="form-section">
                <h3 class="form-section-title">${t('form_section_basic')}</h3>
                
                <div class="form-group">
                  <label class="form-label">${t('form_original_title')} *</label>
                  <input type="text" id="edit-originalTitle" class="form-input" value="${video.originalTitle || video.title || ''}" required>
                </div>
                
                <div class="form-group">
                  <label class="form-label">${t('form_international_title')}</label>
                  <input type="text" id="edit-internationalTitle" class="form-input" value="${video.internationalTitle || ''}">
                </div>
                
                <div class="form-group">
                  <label class="form-label">Stuttur søgusamandráttur / Logline (Faroese) 🇫🇴 <small style="color: var(--color-cloud-grey);">(í mesta lagi 200 tekin)</small></label>
                  <textarea id="edit-loglineFo" class="form-textarea" rows="2" maxlength="200">${video.loglineFo || ''}</textarea>
                  <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="edit-loglineFo-count">${(video.loglineFo || '').length}</span>/200 characters</small>
                </div>
                
                <div class="form-group">
                  <label class="form-label">Um filmin / Synopsis (Faroese) 🇫🇴 <small style="color: var(--color-cloud-grey);">(í mesta lagi 400 tekin)</small></label>
                  <textarea id="edit-synopsisFo" class="form-textarea" rows="4" maxlength="400">${video.synopsisFo || ''}</textarea>
                  <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="edit-synopsisFo-count">${(video.synopsisFo || '').length}</span>/400 characters</small>
                </div>
                
                <div class="form-group">
                  <label class="form-label">Logline (English) 🇬🇧 <small style="color: var(--color-cloud-grey);">(max. 200 characters)</small></label>
                  <textarea id="edit-loglineEn" class="form-textarea" rows="2" maxlength="200">${video.loglineEn || ''}</textarea>
                  <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="edit-loglineEn-count">${(video.loglineEn || '').length}</span>/200 characters</small>
                </div>
                
                <div class="form-group">
                  <label class="form-label">Synopsis (English) 🇬🇧 <small style="color: var(--color-cloud-grey);">(max. 400 characters)</small></label>
                  <textarea id="edit-synopsisEn" class="form-textarea" rows="4" maxlength="400">${video.synopsisEn || ''}</textarea>
                  <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="edit-synopsisEn-count">${(video.synopsisEn || '').length}</span>/400 characters</small>
                </div>
                
                <!-- Poster Image -->
                <div class="form-group">
                  <label class="form-label">${t('form_poster_url')}</label>
                  <div style="display: flex; gap: 12px; flex-direction: column;">
                    <div style="display: flex; gap: 12px; align-items: center;">
                      <input type="file" id="edit-posterFile" accept="image/jpeg,image/png,image/webp" style="display: none;">
                      <button type="button" class="btn btn-secondary btn-small" id="edit-selectPosterBtn">📁 ${t('upload_select_file')}</button>
                      <span id="edit-posterFileName" style="color: var(--color-cloud-grey); font-size: 0.875rem;"></span>
                    </div>
                    <div style="text-align: center; color: var(--color-cloud-grey); font-size: 0.875rem;">or</div>
                    <!-- type="text" instead of "url" — the existing posterUrl is a
                         relative API path and fails HTML5 URL validation; see edit.js. -->
                    <input type="text" id="edit-posterUrl" class="form-input" placeholder="${t('placeholder_poster_url')}" value="${video.posterUrl || ''}">
                  </div>
                  <p class="form-help">Upload file or paste URL • Recommended: 640x960px (2:3 portrait)</p>
                </div>
                
                <!-- Still Image -->
                <div class="form-group">
                  <label class="form-label">${t('form_still_url')}</label>
                  <div style="display: flex; gap: 12px; flex-direction: column;">
                    <div style="display: flex; gap: 12px; align-items: center;">
                      <input type="file" id="edit-stillFile" accept="image/jpeg,image/png,image/webp" style="display: none;">
                      <button type="button" class="btn btn-secondary btn-small" id="edit-selectStillBtn">📁 ${t('upload_select_file')}</button>
                      <span id="edit-stillFileName" style="color: var(--color-cloud-grey); font-size: 0.875rem;"></span>
                    </div>
                    <div style="text-align: center; color: var(--color-cloud-grey); font-size: 0.875rem;">or</div>
                    <input type="text" id="edit-stillUrl" class="form-input" placeholder="${t('placeholder_still_url')}" value="${video.stillUrl || ''}">
                  </div>
                  <p class="form-help">Upload file or paste URL • Recommended: 1920x1080px (16:9 landscape)</p>
                </div>
                
                <div class="form-group">
                  <label class="form-checkbox">
                    <input type="checkbox" id="edit-isFaroese" ${video.isFaroese ? 'checked' : ''}>
                    <span>${t('form_is_faroese')}</span>
                  </label>
                </div>
              </div>
              
              <!-- Crew -->
              <div class="form-section">
                <h3 class="form-section-title">${t('form_section_crew')}</h3>
                
                <!-- Director -->
                <div class="form-group">
                  <label class="form-label">${t('form_director')}</label>
                  <div class="name-input-group">
                    <input type="text" id="edit-directorFirstName" class="form-input" placeholder="${t('placeholder_firstname')}" value="${video.director?.firstName || ''}">
                    <input type="text" id="edit-directorMiddleName" class="form-input" placeholder="${t('placeholder_middlename')}" value="${video.director?.middleName || ''}">
                    <input type="text" id="edit-directorLastName" class="form-input" placeholder="${t('placeholder_lastname')}" value="${video.director?.lastName || ''}">
                  </div>
                </div>
                
                <!-- Screenwriter -->
                <div class="form-group">
                  <label class="form-label">${t('form_screenwriter')}</label>
                  <div class="name-input-group">
                    <input type="text" id="edit-screenwriterFirstName" class="form-input" placeholder="${t('placeholder_firstname')}" value="${video.screenwriter?.firstName || ''}">
                    <input type="text" id="edit-screenwriterMiddleName" class="form-input" placeholder="${t('placeholder_middlename')}" value="${video.screenwriter?.middleName || ''}">
                    <input type="text" id="edit-screenwriterLastName" class="form-input" placeholder="${t('placeholder_lastname')}" value="${video.screenwriter?.lastName || ''}">
                  </div>
                </div>
                
                <!-- Producer -->
                <div class="form-group">
                  <label class="form-label">${t('form_producer')}</label>
                  <div class="name-input-group">
                    <input type="text" id="edit-producerFirstName" class="form-input" placeholder="${t('placeholder_firstname')}" value="${video.producer?.firstName || ''}">
                    <input type="text" id="edit-producerMiddleName" class="form-input" placeholder="${t('placeholder_middlename')}" value="${video.producer?.middleName || ''}">
                    <input type="text" id="edit-producerLastName" class="form-input" placeholder="${t('placeholder_lastname')}" value="${video.producer?.lastName || ''}">
                  </div>
                </div>
                
                <!-- Director of Photography -->
                <div class="form-group">
                  <label class="form-label">${t('form_dop')}</label>
                  <div class="name-input-group">
                    <input type="text" id="edit-dopFirstName" class="form-input" placeholder="${t('placeholder_firstname')}" value="${video.dop?.firstName || ''}">
                    <input type="text" id="edit-dopMiddleName" class="form-input" placeholder="${t('placeholder_middlename')}" value="${video.dop?.middleName || ''}">
                    <input type="text" id="edit-dopLastName" class="form-input" placeholder="${t('placeholder_lastname')}" value="${video.dop?.lastName || ''}">
                  </div>
                </div>
                
                <!-- Editor -->
                <div class="form-group">
                  <label class="form-label">${t('form_editor')}</label>
                  <div class="name-input-group">
                    <input type="text" id="edit-editorFirstName" class="form-input" placeholder="${t('placeholder_firstname')}" value="${video.editor?.firstName || ''}">
                    <input type="text" id="edit-editorMiddleName" class="form-input" placeholder="${t('placeholder_middlename')}" value="${video.editor?.middleName || ''}">
                    <input type="text" id="edit-editorLastName" class="form-input" placeholder="${t('placeholder_lastname')}" value="${video.editor?.lastName || ''}">
                  </div>
                </div>
                
                <!-- Music Composer -->
                <div class="form-group">
                  <label class="form-label">${t('form_composer')}</label>
                  <div class="name-input-group">
                    <input type="text" id="edit-composerFirstName" class="form-input" placeholder="${t('placeholder_firstname')}" value="${video.composer?.firstName || ''}">
                    <input type="text" id="edit-composerMiddleName" class="form-input" placeholder="${t('placeholder_middlename')}" value="${video.composer?.middleName || ''}">
                    <input type="text" id="edit-composerLastName" class="form-input" placeholder="${t('placeholder_lastname')}" value="${video.composer?.lastName || ''}">
                  </div>
                </div>
                
                <!-- Main Cast -->
                <div class="form-group">
                  <label class="form-label">${t('form_main_cast')}</label>
                  <textarea id="edit-mainCast" class="form-textarea" rows="2" placeholder="First Middle Last, First Middle Last, ...">${(video.mainCast || []).map((c) => `${c.firstName || ''} ${c.middleName || ''} ${c.lastName || ''}`.trim()).join(', ')}</textarea>
                  <small style="color: var(--color-cloud-grey); font-size: 0.875rem;">Separate multiple cast members with commas</small>
                </div>
                
                <!-- Key Crew -->
                <div class="form-group">
                  <label class="form-label">${t('form_key_crew')}</label>
                  <textarea id="edit-keyCrew" class="form-textarea" rows="2" placeholder="First Middle Last, First Middle Last, ...">${(video.keyCrew || []).map((c) => `${c.firstName || ''} ${c.middleName || ''} ${c.lastName || ''}`.trim()).join(', ')}</textarea>
                  <small style="color: var(--color-cloud-grey); font-size: 0.875rem;">Separate multiple crew members with commas</small>
                </div>
              </div>
              
              <!-- Production Companies -->
              <div class="form-section">
                <h3 class="form-section-title">Production</h3>
                
                <div class="form-group">
                  <label class="form-label">${t('form_production_company')}</label>
                  <input type="text" id="edit-productionCompany" class="form-input" value="${video.productionCompany || ''}">
                </div>
                
                <div class="form-group">
                  <label class="form-label">${t('form_coproduction_company')}</label>
                  <input type="text" id="edit-coproductionCompany" class="form-input" value="${video.coproductionCompany || ''}">
                </div>
              </div>
              
              <!-- Technical Details -->
              <div class="form-section">
                <h3 class="form-section-title">${t('form_section_technical')}</h3>
                
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
                  <div class="form-group">
                    <label class="form-label">${t('form_runtime_hours')}</label>
                    <input type="number" id="edit-runtimeHours" class="form-input" min="0" max="10" value="${video.runtimeHours || 0}">
                  </div>
                  
                  <div class="form-group">
                    <label class="form-label">${t('form_runtime_minutes')}</label>
                    <input type="number" id="edit-runtimeMinutes" class="form-input" min="0" max="59" value="${video.runtimeMinutes || 0}">
                  </div>
                </div>
                
                <div class="form-group">
                  <label class="form-label">${t('form_language')}</label>
                  <input type="text" id="edit-language" class="form-input" value="${video.language || ''}">
                </div>
                
                <div class="form-group">
                  <label class="form-label">${t('form_subtitles')}</label>
                  <input type="text" id="edit-subtitles" class="form-input" value="${video.subtitles || ''}">
                </div>
                
                <div class="form-group">
                  <label class="form-label">${t('form_production_year')}</label>
                  <input type="number" id="edit-productionYear" class="form-input" min="1900" max="2100" value="${video.productionYear || video.year || ''}">
                </div>
                
                <div class="form-group">
                  <label class="form-label">${t('form_imdb_link')}</label>
                  <input type="url" id="edit-imdbLink" class="form-input" value="${video.imdbLink || ''}">
                </div>
              </div>
              
              <!-- Pricing / Cost -->
              <div class="form-section">
                <h3 class="form-section-title">KOSTNAÐUR * / COST *</h3>
                
                <div class="form-group">
                  <label class="form-label">Pricing Type *</label>
                  <select id="edit-pricingType" class="form-input" required>
                    <option value="standard" ${(video.pricingType || 'standard') === 'standard' ? 'selected' : ''}>Standard Pricing</option>
                    <option value="free" ${video.pricingType === 'free' ? 'selected' : ''}>Ókeypis / Free</option>
                    <option value="custom" ${video.pricingType === 'custom' ? 'selected' : ''}>Onnur upphædd / Custom Amount</option>
                  </select>
                </div>
                
                <div class="form-group" id="edit-pricing-tier-group" style="display: ${(video.pricingType || 'standard') === 'standard' ? 'block' : 'none'};">
                  <label class="form-label">Pricing Tier</label>
                  <select id="edit-pricingTier" class="form-input">
                    <option value="">Select pricing tier...</option>
                    <!-- Options populated dynamically -->
                  </select>
                </div>
                
                <div class="form-group" id="edit-custom-price-group" style="display: ${video.pricingType === 'custom' ? 'block' : 'none'};">
                  <label class="form-label">Custom Price (DKK)</label>
                  <input type="number" id="edit-customPrice" class="form-input" min="0" step="0.01" value="${video.customPrice ? (video.customPrice / 100).toFixed(2) : ''}" placeholder="0.00">
                  <p class="form-help">Enter price in DKK (e.g., 29.00)</p>
                </div>
                
                <div class="form-group" id="edit-rental-duration-group" style="display: ${video.pricingType === 'free' ? 'none' : 'block'};">
                  <label class="form-label">Rental Duration (hours)</label>
                  <input type="number" id="edit-rentalDuration" class="form-input" min="1" max="168" value="${video.rentalDurationHours || 48}">
                  <small style="color: var(--color-cloud-grey); font-size: 0.875rem;">How long users can watch (default: 48h)</small>
                </div>
              </div>
              
              <!-- Bank Information -->
              <div class="form-section" id="edit-bank-info-section" style="display: ${video.pricingType === 'free' ? 'none' : 'block'};">
                <h3 class="form-section-title">BANKAUPPLÝSINGAR / BANK INFORMATION</h3>
                
                <div class="form-group">
                  <label class="form-label" for="edit-bankName">Banki / Bank *</label>
                  <input type="text" id="edit-bankName" class="form-input" value="${video.bankName || ''}">
                </div>

                <div class="form-group">
                  <label class="form-label" for="edit-bankRegistrationNo">Skrás. nr. / Registration no.</label>
                  <input type="text" id="edit-bankRegistrationNo" class="form-input" value="${video.bankRegistrationNo || ''}">
                </div>

                <div class="form-group">
                  <label class="form-label" for="edit-bankAccountNo">Konto nr. / Account no.</label>
                  <input type="text" id="edit-bankAccountNo" class="form-input" value="${video.bankAccountNo || ''}">
                </div>
              </div>
              
              <div class="flex flex-gap-md mt-md" style="position: sticky; bottom: 0; background: var(--color-storm-grey); padding: 1rem 0; margin-top: 2rem;">
                <button type="submit" class="btn btn-primary">${t('action_save')}</button>
                <button type="button" class="btn btn-secondary" data-action="close-modal">${t('action_cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      `;

      modal.style.display = 'block';

      // Image file upload handlers
      let selectedPosterFile = null;
      let selectedStillFile = null;

      const posterFileInput = document.getElementById('edit-posterFile');
      const stillFileInput = document.getElementById('edit-stillFile');
      const selectPosterBtn = document.getElementById('edit-selectPosterBtn');
      const selectStillBtn = document.getElementById('edit-selectStillBtn');
      const posterFileName = document.getElementById('edit-posterFileName');
      const stillFileName = document.getElementById('edit-stillFileName');

      selectPosterBtn.addEventListener('click', () => posterFileInput.click());
      selectStillBtn.addEventListener('click', () => stillFileInput.click());

      posterFileInput.addEventListener('change', (e) => {
        if (e.target.files.length > 0) {
          selectedPosterFile = e.target.files[0];
          posterFileName.textContent = selectedPosterFile.name;
          // Clear URL input if file is selected
          document.getElementById('edit-posterUrl').value = '';
        }
      });

      stillFileInput.addEventListener('change', (e) => {
        if (e.target.files.length > 0) {
          selectedStillFile = e.target.files[0];
          stillFileName.textContent = selectedStillFile.name;
          // Clear URL input if file is selected
          document.getElementById('edit-stillUrl').value = '';
        }
      });

      // Character counters for textareas
      const editLoglineFo = document.getElementById('edit-loglineFo');
      const editLoglineEn = document.getElementById('edit-loglineEn');
      const editSynopsisFo = document.getElementById('edit-synopsisFo');
      const editSynopsisEn = document.getElementById('edit-synopsisEn');

      [editLoglineFo, editLoglineEn, editSynopsisFo, editSynopsisEn].forEach((textarea) => {
        if (textarea) {
          const counterId = `${textarea.id}-count`;
          const counter = document.getElementById(counterId);
          textarea.addEventListener('input', () => {
            if (counter) counter.textContent = textarea.value.length;
          });
        }
      });

      // Pricing type change handler for edit form
      const editPricingTypeSelect = document.getElementById('edit-pricingType');
      const editPricingTierGroup = document.getElementById('edit-pricing-tier-group');
      const editPricingTierSelect = document.getElementById('edit-pricingTier');
      const editCustomPriceGroup = document.getElementById('edit-custom-price-group');
      const editRentalDurationGroup = document.getElementById('edit-rental-duration-group');
      const editBankInfoSection = document.getElementById('edit-bank-info-section');
      const _editBankNameInput = document.getElementById('edit-bankName');
      const _editBankRegistrationNoInput = document.getElementById('edit-bankRegistrationNo');
      const _editBankAccountNoInput = document.getElementById('edit-bankAccountNo');
      const editCategorySelect = document.getElementById('edit-category');

      function updateEditPricingFields() {
        const pricingType = editPricingTypeSelect.value;
        const category = editCategorySelect.value;

        if (pricingType === 'free') {
          editPricingTierGroup.style.display = 'none';
          editCustomPriceGroup.style.display = 'none';
          editRentalDurationGroup.style.display = 'none';
          editBankInfoSection.style.display = 'none';
        } else {
          // Show rental duration and bank info for paid content
          editRentalDurationGroup.style.display = 'block';
          editBankInfoSection.style.display = 'block';

          if (pricingType === 'custom') {
            editPricingTierGroup.style.display = 'none';
            editCustomPriceGroup.style.display = 'block';
          } else if (pricingType === 'standard') {
            editCustomPriceGroup.style.display = 'none';
            editPricingTierGroup.style.display = 'block';

            // Populate pricing tier options based on category
            editPricingTierSelect.innerHTML = '<option value="">Select pricing tier...</option>';

            if (category === 'short') {
              editPricingTierSelect.innerHTML += `
              <option value="short_under_3mo" ${video.pricingTier === 'short_under_3mo' ? 'selected' : ''}>Yngri enn 3 mðr. / Under 3 months: DKK 29</option>
              <option value="short_under_1yr" ${video.pricingTier === 'short_under_1yr' ? 'selected' : ''}>Yngri enn 1 ár / Under 1 year: DKK 19</option>
              <option value="short_under_4yr" ${video.pricingTier === 'short_under_4yr' ? 'selected' : ''}>Yngri enn 4 ár / Under 4 years: DKK 7</option>
              <option value="short_over_4yr" ${video.pricingTier === 'short_over_4yr' ? 'selected' : ''}>Eldri enn 4 ár / Over 4 years: DKK 0</option>
            `;
            } else if (category === 'feature') {
              editPricingTierSelect.innerHTML += `
              <option value="feature_under_2yr" ${video.pricingTier === 'feature_under_2yr' ? 'selected' : ''}>Yngri enn 2 ár / Under 2 years: DKK 59</option>
              <option value="feature_over_2yr" ${video.pricingTier === 'feature_over_2yr' ? 'selected' : ''}>Eldri enn 2 ár / Over 2 years: DKK 39</option>
              <option value="feature_over_4yr" ${video.pricingTier === 'feature_over_4yr' ? 'selected' : ''}>Eldri enn 4 ár / Over 4 years: DKK 0</option>
            `;
            } else if (category === 'documentary_short') {
              editPricingTierSelect.innerHTML += `
              <option value="doc_short_under_1yr" ${video.pricingTier === 'doc_short_under_1yr' ? 'selected' : ''}>Yngri enn 1 ár / Under 1 year: DKK 19</option>
              <option value="doc_short_under_4yr" ${video.pricingTier === 'doc_short_under_4yr' ? 'selected' : ''}>Yngri enn 4 ár / Under 4 years: DKK 7</option>
              <option value="doc_short_over_4yr" ${video.pricingTier === 'doc_short_over_4yr' ? 'selected' : ''}>Eldri enn 4 ár / Over 4 years: DKK 0</option>
            `;
            } else if (category === 'documentary_long') {
              editPricingTierSelect.innerHTML += `
              <option value="doc_long_under_2yr" ${video.pricingTier === 'doc_long_under_2yr' ? 'selected' : ''}>Yngri enn 2 ár / Under 2 years: DKK 59</option>
              <option value="doc_long_under_4yr" ${video.pricingTier === 'doc_long_under_4yr' ? 'selected' : ''}>Yngri enn 4 ár / Under 4 years: DKK 39</option>
              <option value="doc_long_over_4yr" ${video.pricingTier === 'doc_long_over_4yr' ? 'selected' : ''}>Eldri enn 4 ár / Over 4 years: DKK 0</option>
            `;
            }
          }
        }
      }

      editPricingTypeSelect.addEventListener('change', updateEditPricingFields);
      editCategorySelect.addEventListener('change', updateEditPricingFields);

      // Initialize pricing fields
      updateEditPricingFields();

      // Close modal handlers
      modal.querySelectorAll('[data-action="close-modal"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          modal.style.display = 'none';
        });
      });

      // Form submission
      document.getElementById('edit-form').addEventListener('submit', async (e) => {
        e.preventDefault();

        // Helper to parse name lists
        const parseNameList = (text) => {
          if (!text) return null;
          return text
            .split(',')
            .map((name) => {
              const parts = name.trim().split(' ');
              return {
                firstName: parts[0] || '',
                middleName: parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
                lastName: parts[parts.length - 1] || '',
              };
            })
            .filter((n) => n.firstName || n.lastName);
        };

        const getNameInput = (prefix) => {
          const firstName = document.getElementById(`edit-${prefix}FirstName`)?.value || '';
          const middleName = document.getElementById(`edit-${prefix}MiddleName`)?.value || '';
          const lastName = document.getElementById(`edit-${prefix}LastName`)?.value || '';

          if (!firstName && !middleName && !lastName) return null;
          return { firstName, middleName, lastName };
        };

        const updatedVideo = {
          videoId: video.videoId,
          fileName: video.fileName,
          state: video.state,
          published: video.published,
          uploadedAt: video.uploadedAt || video.createdAt || new Date().toISOString(),

          // Updated fields
          category: document.getElementById('edit-category').value,
          originalTitle: document.getElementById('edit-originalTitle').value,
          internationalTitle: document.getElementById('edit-internationalTitle').value,
          loglineFo: document.getElementById('edit-loglineFo').value,
          loglineEn: document.getElementById('edit-loglineEn').value,
          synopsisFo: document.getElementById('edit-synopsisFo').value,
          synopsisEn: document.getElementById('edit-synopsisEn').value,
          posterUrl: document.getElementById('edit-posterUrl').value,
          stillUrl: document.getElementById('edit-stillUrl').value,

          // Pricing
          pricingType: document.getElementById('edit-pricingType').value,
          pricingTier: document.getElementById('edit-pricingTier').value || null,
          customPrice: document.getElementById('edit-customPrice').value
            ? Math.round(parseFloat(document.getElementById('edit-customPrice').value) * 100)
            : null,

          // Crew
          director: getNameInput('director'),
          screenwriter: getNameInput('screenwriter'),
          producer: getNameInput('producer'),
          dop: getNameInput('dop'),
          editor: getNameInput('editor'),
          composer: getNameInput('composer'),

          // Cast and Crew arrays
          mainCast: parseNameList(document.getElementById('edit-mainCast').value),
          keyCrew: parseNameList(document.getElementById('edit-keyCrew').value),

          // Companies
          productionCompany: document.getElementById('edit-productionCompany').value,
          coproductionCompany: document.getElementById('edit-coproductionCompany').value,

          // Technical
          runtimeHours: parseInt(document.getElementById('edit-runtimeHours').value, 10) || 0,
          runtimeMinutes: parseInt(document.getElementById('edit-runtimeMinutes').value, 10) || 0,
          language: document.getElementById('edit-language').value,
          subtitles: document.getElementById('edit-subtitles').value,
          productionYear:
            parseInt(document.getElementById('edit-productionYear').value, 10) || new Date().getFullYear(),
          imdbLink: document.getElementById('edit-imdbLink').value,

          // Rental Settings (only if not free)
          rentalDurationHours:
            document.getElementById('edit-pricingType').value === 'free'
              ? null
              : parseInt(document.getElementById('edit-rentalDuration').value, 10) || 48,

          // Bank information (only if not free)
          bankName:
            document.getElementById('edit-pricingType').value === 'free'
              ? null
              : document.getElementById('edit-bankName').value,
          bankRegistrationNo:
            document.getElementById('edit-pricingType').value === 'free'
              ? null
              : document.getElementById('edit-bankRegistrationNo').value,
          bankAccountNo:
            document.getElementById('edit-pricingType').value === 'free'
              ? null
              : document.getElementById('edit-bankAccountNo').value,
        };

        // Upload images if provided (file takes priority over URL)
        try {
          if (selectedPosterFile) {
            const posterResult = await api.uploadImage(selectedPosterFile, 'poster', videoId);
            updatedVideo.posterUrl = posterResult.url;
            if (posterResult.imageId) updatedVideo.posterAssetId = posterResult.imageId;
          }

          if (selectedStillFile) {
            const stillResult = await api.uploadImage(selectedStillFile, 'still', videoId);
            updatedVideo.stillUrl = stillResult.url;
            if (stillResult.imageId) updatedVideo.stillAssetId = stillResult.imageId;
          }
        } catch (imageError) {
          console.error('Image upload error:', imageError);
          alert(`Video updated, but image upload failed: ${imageError.message}`);
        }

        // Save changes to database
        try {
          await api.updateVideo(videoId, updatedVideo);
          alert(t('msg_success'));
          modal.style.display = 'none';
          await loadVideos();
        } catch (error) {
          console.error('Failed to save changes:', error);
          alert(`Failed to save changes: ${error.message}`);
        }
      });
    } catch (error) {
      alert(`Failed to load video details: ${error.message}`);
    }
  }

  // Show status modal
  async function showStatusModal(videoId) {
    try {
      const status = await api.getVideoStatus(videoId);
      alert(`Status: ${JSON.stringify(status, null, 2)}`);
    } catch (error) {
      alert(`Failed to get status: ${error.message}`);
    }
  }

  // Handle delete
  async function handleDelete(videoId) {
    if (!confirm('Are you sure you want to delete this video? The video will be hidden but can be restored later.')) {
      return;
    }

    try {
      await api.deleteVideo(videoId);
      alert('✅ Video deleted successfully!');
      await loadVideos();
    } catch (error) {
      alert(`❌ Failed to delete video: ${error.message}`);
    }
  }

  // Handle publish/unpublish
  async function handlePublish(videoId, currentlyPublished) {
    const action = currentlyPublished ? 'unpublish' : 'publish';
    const confirmMsg = currentlyPublished
      ? 'Unpublish this video? It will be hidden from the public site.'
      : 'Publish this video? It will be visible on the public site.';

    if (!confirm(confirmMsg)) {
      return;
    }

    try {
      const newPublished = !currentlyPublished;
      await api.togglePublished(videoId, newPublished);
      alert(`✅ Video ${newPublished ? 'published' : 'unpublished'} successfully!`);
      await loadVideos();
    } catch (error) {
      alert(`❌ Failed to ${action} video: ${error.message}`);
    }
  }

  // Handle approve (admin only)
  async function handleApprove(videoId) {
    const confirmed = await showConfirm(
      'Approve this video? It will be marked as approved and can be published.',
      'Approve Video',
    );

    if (!confirmed) return;

    try {
      await api.approveVideo(videoId);
      await showModal('Video approved successfully!', 'success', 'Approved');
      await loadVideos();
    } catch (error) {
      await showModal(`Failed to approve video:\n\n${error.message}`, 'error', 'Approval Failed');
    }
  }

  // Handle reject (admin only)
  async function handleReject(videoId) {
    const reason = await showPrompt('Reject this video? Please enter a reason (optional):', '', 'Reject Video');

    if (reason === null) return; // User cancelled

    try {
      await api.rejectVideo(videoId, reason);
      await showModal('Video rejected.', 'success', 'Rejected');
      await loadVideos();
    } catch (error) {
      await showModal(`Failed to reject video:\n\n${error.message}`, 'error', 'Rejection Failed');
    }
  }

  // Handle retry encoding
  async function handleRetryEncoding(videoId) {
    const confirmed = await showConfirm(
      'Retry encoding for this video? This will restart the encoding process.',
      'Retry Encoding',
    );

    if (!confirmed) return;

    try {
      await api.retryEncoding(videoId);
      await showModal('Encoding retry triggered! The video will be processed shortly.', 'success', 'Encoding Started');
      await loadVideos();
    } catch (error) {
      await showModal(`Failed to retry encoding:\n\n${error.message}`, 'error', 'Retry Failed');
    }
  }

  async function handleForceReencode(videoId) {
    const confirmed = await showConfirm(
      'Force re-encode this video?\n\n' +
        'This will re-encode the video with the latest encoder settings.\n' +
        'Useful when:\n' +
        '• New quality profiles are added (e.g., 4K, 8K)\n' +
        '• Encoding settings are improved\n' +
        '• New codec or format is supported\n\n' +
        'The video will remain published and accessible during re-encoding.',
      'Force Re-Encode',
    );

    if (!confirmed) return;

    try {
      // Call the same retry encoding endpoint - it works for ready videos too
      await api.retryEncoding(videoId);
      await showModal(
        'Re-encoding started! The video will be processed with the latest encoder settings.\n\n' +
          'The video remains published and accessible during this process.',
        'success',
        'Re-Encoding Started',
      );
      await loadVideos();
    } catch (error) {
      await showModal(`Failed to start re-encoding:\n\n${error.message}`, 'error', 'Re-Encode Failed');
    }
  }

  // Initial load
  await loadVideos();

  // Check for incomplete uploads (video uploaded but encoding not triggered)
  const lastUploadedVideoId = localStorage.getItem('lastUploadedVideoId');
  const lastUploadedVideoName = localStorage.getItem('lastUploadedVideoName');

  if (lastUploadedVideoId && lastUploadedVideoName) {
    // Show reminder about the incomplete upload
    const confirmed = await showConfirm(
      `It looks like your video "${lastUploadedVideoName}" was uploaded successfully, but encoding may not have started.\n\n` +
        `Would you like to retry encoding for this video now?`,
      'Incomplete Upload Detected',
    );

    // Clear the localStorage
    localStorage.removeItem('lastUploadedVideoId');
    localStorage.removeItem('lastUploadedVideoName');

    // If user clicked OK, trigger encoding
    if (confirmed) {
      try {
        await api.retryEncoding(lastUploadedVideoId);
        await showModal('Encoding triggered! Your video will be processed shortly.', 'success', 'Encoding Started');
        await loadVideos();
      } catch (error) {
        await showModal(
          'Failed to trigger encoding:\n\n' +
            error.message +
            '\n\nPlease find your video in the list and click "🔄 Retry Encoding" manually.',
          'error',
          'Retry Failed',
        );
      }
    }
  }

  // Check for ongoing uploads (interrupted uploads with progress in localStorage)
  const incompleteUploads = api.getAllIncompleteUploads();

  if (incompleteUploads.length > 0) {
    // Sort by most recent first
    incompleteUploads.sort((a, b) => b.lastUpdated - a.lastUpdated);

    for (const upload of incompleteUploads.slice(0, 3)) {
      // Show max 3 most recent
      const percentage = Math.round((upload.uploadedChunks.length / upload.totalChunks) * 100);
      const _fileSizeMB = (upload.fileSize / (1024 * 1024)).toFixed(2);
      const timeAgo = formatTimeAgo(upload.lastUpdated);

      console.log(`📤 Incomplete upload detected: ${upload.fileName} (${percentage}%) - ${timeAgo}`);
    }

    // Show a single notification for all incomplete uploads
    const mostRecent = incompleteUploads[0];
    const percentage = Math.round((mostRecent.uploadedChunks.length / mostRecent.totalChunks) * 100);

    const message =
      incompleteUploads.length === 1
        ? `📤 Incomplete upload detected!\n\n` +
          `File: ${mostRecent.fileName}\n` +
          `Progress: ${percentage}%\n` +
          `Last updated: ${formatTimeAgo(mostRecent.lastUpdated)}\n\n` +
          `This upload was interrupted. To resume:\n` +
          `1. Go to Upload or Edit page\n` +
          `2. Select the same file\n` +
          `3. The upload will automatically continue from ${percentage}%\n\n` +
          `Clear this notification?`
        : `📤 ${incompleteUploads.length} incomplete uploads detected!\n\n` +
          `Most recent: ${mostRecent.fileName} (${percentage}%)\n\n` +
          `These uploads were interrupted. To resume any of them:\n` +
          `1. Go to Upload or Edit page\n` +
          `2. Select the same file\n` +
          `3. The upload will automatically continue\n\n` +
          `Clear all notifications?`;

    const clear = await showConfirm(message, 'Incomplete Uploads', 'warning');

    if (clear) {
      // Clear all incomplete upload progress
      for (const upload of incompleteUploads) {
        api.clearUploadProgress(upload.videoId);
      }
      await showModal('Upload notifications cleared.', 'info', 'Cleared');
    }
  }

  // Helper function for time ago formatting
  function formatTimeAgo(timestamp) {
    const now = Date.now();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  }

  // Capture our mounted grid element so any prior orphaned interval can
  // detect it's running against a detached node and self-clean. Must happen
  // before startStatusPolling so the first tick sees the captured ref.
  mountedGrid = document.getElementById('video-grid');

  // Start polling for status updates
  startStatusPolling();
}

// Active state styling
const style = document.createElement('style');
style.textContent = `
  .btn-secondary.active {
    background-color: var(--color-nordic-blue);
    color: var(--color-arctic-white);
    border-color: var(--color-nordic-blue);
  }
`;
if (!document.getElementById('manage-styles')) {
  style.id = 'manage-styles';
  document.head.appendChild(style);
}
